import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Briefcase, 
  DollarSign, 
  Gift, 
  AlertCircle, 
  Users, 
  Settings, 
  HelpCircle,
  Search,
  Bell,
  Plus,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Store,
  ArrowLeftRight,
  CheckCheck,
  Layers,
  FileSpreadsheet,
  Megaphone,
  Inbox,
  ShieldCheck,
  UserCircle,
  LifeBuoy,
  ShieldAlert,
  ScrollText,
  UserCog,
  ClipboardList,
  Building2,
  Plug
} from 'lucide-react';
import theme from '../theme';
import GuidedTour from './GuidedTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppLayout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, location.pathname]);

  const markRead = async (ids) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/mark-read`, { ids }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ids.includes(n.id) ? { ...n, is_read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - ids.length));
    } catch (err) {
      // silent
    }
  };

  const markAllRead = () => {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length > 0) markRead(unreadIds);
  };

  const handleNotifClick = (notif) => {
    if (!notif.is_read) markRead([notif.id]);
    if (notif.link) navigate(notif.link);
    setNotifOpen(false);
  };

  // P8.5: Handle search - navigate to /{role}/search?q=...
  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      const role = user?.role || 'buyer';
      navigate(`/${role}/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  // Role-specific navigation items
  const getNavItems = () => {
    const role = user?.role;
    
    const navMap = {
      creator: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/creator/dashboard' },
        { icon: Briefcase, label: 'Portfolios', path: '/creator/portfolios' },
        { icon: Store, label: 'NFT Art Studio', path: '/creator/nft-art' },
        { icon: DollarSign, label: 'Payouts', path: '/creator/payouts' },
        { icon: DollarSign, label: 'Billing', path: '/creator/billing' },
        { icon: Gift, label: 'Utilities', path: '/creator/utilities' },
        { icon: ClipboardList, label: 'Architect Requests', path: '/creator/architect-requests' },
        { icon: AlertCircle, label: 'Disputes', path: '/creator/disputes' },
        { icon: Settings, label: 'Settings', path: '/creator/settings' },
        { icon: HelpCircle, label: 'Help', path: '/help' },
      ],
      broker: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/broker/dashboard' },
        { icon: LayoutDashboard, label: 'Overview', path: '/broker/overview' },
        { icon: Inbox, label: 'Assignment Inbox', path: '/broker/inbox' },
        { icon: Briefcase, label: 'Portfolios', path: '/broker/portfolios' },
        { icon: Store, label: 'Directory', path: '/broker/directory' },
        { icon: DollarSign, label: 'Distributions', path: '/broker/distributions' },
        { icon: DollarSign, label: 'Payouts', path: '/broker/payouts' },
        { icon: ShieldCheck, label: 'Compliance', path: '/broker/compliance' },
        { icon: ShieldCheck, label: 'My Utilities', path: '/broker/utility-approval' },
        { icon: UserCircle, label: 'Profile', path: '/broker/profile' },
        { icon: Bell, label: 'Notifications', path: '/broker/notifications' },
        { icon: FileSpreadsheet, label: 'Tax Center', path: '/broker/tax' },
        { icon: AlertCircle, label: 'Disputes', path: '/broker/disputes' },
        { icon: Settings, label: 'Settings', path: '/broker/settings' },
        { icon: HelpCircle, label: 'Help', path: '/help' },
      ],
      buyer: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/buyer/dashboard' },
        { icon: Briefcase, label: 'Holdings', path: '/buyer/holdings' },
        { icon: Layers, label: 'NFT Library', path: '/buyer/library' },
        { icon: DollarSign, label: 'Distributions', path: '/buyer/distributions' },
        { icon: DollarSign, label: 'Payouts', path: '/buyer/payouts' },
        { icon: Gift, label: 'Kickback Gifts', path: '/buyer/gifts' },
        { icon: Gift, label: 'My Claims', path: '/buyer/claims' },
        { icon: Gift, label: 'Utilities', path: '/buyer/utilities' },
        { icon: Store, label: 'Marketplace', path: '/buyer/marketplace' },
        { icon: ArrowLeftRight, label: 'Transfers', path: '/buyer/transfers' },
        { icon: Bell, label: 'Notifications', path: '/buyer/notifications' },
        { icon: Megaphone, label: 'Announcements', path: '/buyer/announcements' },
        { icon: FileSpreadsheet, label: 'Tax Center', path: '/buyer/tax' },
        { icon: AlertCircle, label: 'Disputes', path: '/buyer/disputes' },
        { icon: Settings, label: 'Settings', path: '/buyer/settings' },
        { icon: HelpCircle, label: 'Help', path: '/help' },
      ],
      admin: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard?tab=overview' },
        { icon: ShieldCheck, label: 'Compliance', path: '/admin/compliance' },
        { icon: Users, label: 'Providers', path: '/admin/providers' },
        { icon: DollarSign, label: 'Distributions', path: '/admin/distributions' },
        { icon: LifeBuoy, label: 'Support', path: '/admin/support' },
        { icon: ShieldAlert, label: 'Fraud', path: '/admin/fraud' },
        { icon: ShieldCheck, label: 'Utility Queue', path: '/admin/utilities' },
        { icon: UserCog, label: 'Architects', path: '/admin/architects' },
        { icon: ClipboardList, label: 'Architect Requests', path: '/admin/architect-requests' },
        { icon: UserCog, label: 'AI Settings', path: '/admin/ai-settings' },
        { icon: ScrollText, label: 'Audit Log', path: '/admin/audit' },
        { icon: Bell, label: 'Notifications', path: '/admin/notifications' },
        { icon: Building2, label: 'Enterprise Inquiries', path: '/admin/enterprise-inquiries' },
        { icon: Plug, label: 'Launch Bridge', path: '/admin/launch-bridge' },
        { icon: Users, label: 'Users', path: '/admin/dashboard?tab=users' },
        { icon: Briefcase, label: 'Portfolios', path: '/admin/dashboard?tab=portfolios' },
        { icon: AlertCircle, label: 'Disputes', path: '/admin/dashboard?tab=disputes' },
        { icon: Settings, label: 'Settings', path: '/admin/dashboard?tab=settings' },
        { icon: HelpCircle, label: 'Help', path: '/help' },
      ],
      architect: [
        { icon: UserCog, label: 'Profile', path: '/architect/profile' },
        { icon: ClipboardList, label: 'Assigned Requests', path: '/architect/requests' },
        { icon: HelpCircle, label: 'Help', path: '/help' },
      ],
    };

    return navMap[role] || [];
  };

  // Role-specific "Create" CTA
  const getCreateCTA = () => {
    const role = user?.role;
    const ctaMap = {
      creator: { label: 'Create Portfolio', action: () => navigate('/creator/portfolios/new') },
      broker: { label: 'Review Opportunities', action: () => navigate('/broker/portfolios') },
      buyer: { label: 'Browse NFTs', action: () => navigate('/buyer/marketplace') },
      admin: { label: 'Add User', action: () => navigate('/admin/dashboard?tab=invites&openInvite=1') },
    };
    return ctaMap[role];
  };

  const navItems = getNavItems();
  const createCTA = getCreateCTA();

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      background: theme.colors.bg.page,
      fontFamily: theme.fonts.ui 
    }}>
      {/* Sidebar */}
      <aside
        style={{
          width: sidebarOpen ? '280px' : '80px',
          background: theme.colors.bg.card,
          borderRight: `1px solid ${theme.colors.borders}`,
          transition: 'width 0.3s',
          position: 'fixed',
          height: '100vh',
          overflowY: 'auto',
          zIndex: 40,
        }}
      >
        <div style={{ padding: '24px' }}>
          {/* Logo */}
          <div 
            onClick={() => navigate('/dashboard')}
            style={{ 
              cursor: 'pointer', 
              marginBottom: '32px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent.teal})`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: theme.fonts.heading,
              color: '#FFF',
              fontWeight: 'bold',
              fontSize: '20px'
            }}>
              KC
            </div>
            {sidebarOpen && (
              <div>
                <div style={{ 
                  fontFamily: theme.fonts.heading, 
                  fontSize: '18px',
                  fontWeight: '700',
                  color: theme.colors.text.primary 
                }}>
                  Kickback
                </div>
                <div style={{ 
                  fontSize: '12px',
                  color: theme.colors.text.muted 
                }}>
                  Contracts
                </div>
              </div>
            )}
          </div>

          {/* Navigation Items */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              // Handle query-param paths (e.g., /admin/dashboard?tab=users)
              const [itemBase, itemQuery] = item.path.split('?');
              const isActive = itemQuery
                ? location.pathname === itemBase && location.search.includes(itemQuery)
                : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-tour-id={`${user?.role}-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: isActive ? `${theme.colors.primary}15` : 'transparent',
                    color: isActive ? theme.colors.primary : theme.colors.text.secondary,
                    border: 'none',
                    borderRadius: theme.radius.md,
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: isActive ? '600' : '500',
                    transition: 'all 0.2s',
                    width: '100%',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = `${theme.colors.bg.secondary}`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Icon size={20} />
                  {sidebarOpen && <span>{item.label}</span>}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Sidebar Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'absolute',
            bottom: '24px',
            left: sidebarOpen ? '24px' : '20px',
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.borders}`,
            borderRadius: '8px',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </aside>

      {/* Main Content */}
      <div style={{ 
        marginLeft: sidebarOpen ? '280px' : '80px',
        flex: 1,
        transition: 'margin-left 0.3s',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Top Bar */}
        <header style={{
          height: '72px',
          background: theme.colors.bg.card,
          borderBottom: `1px solid ${theme.colors.borders}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 32px',
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}>
          {/* Mobile Menu Toggle + Search */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '12px',
            flex: 1,
            maxWidth: '500px'
          }}>
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.borders}`,
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Toggle menu"
            >
              <Menu size={20} color={theme.colors.text.primary} />
            </button>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: theme.colors.bg.secondary,
              padding: '10px 16px',
              borderRadius: theme.radius.cta,
              border: `1px solid ${theme.colors.borders}`,
              flex: 1,
            }}>
              <Search size={20} color={theme.colors.text.muted} />
              <input
                type="text"
                placeholder="Search portfolios, NFTs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearch}
                data-testid="global-search-input"
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontSize: '15px',
                  color: theme.colors.text.primary,
                  width: '100%',
                  fontFamily: theme.fonts.ui,
                }}
              />
            </div>
          </div>

          {/* Right Section */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Create CTA */}
            {createCTA && (
              <button
                onClick={createCTA.action}
                style={{
                  background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent.teal})`,
                  color: '#FFF',
                  border: 'none',
                  borderRadius: theme.radius.cta,
                  padding: '10px 24px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: theme.colors.shadows.md,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = theme.colors.shadows.lg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.colors.shadows.md;
                }}
              >
                <Plus size={18} />
                {createCTA.label}
              </button>
            )}

            {/* Notifications Bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }}
                data-testid="notifications-bell"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  position: 'relative',
                }}
              >
                <Bell size={22} color={theme.colors.text.secondary} />
                {unreadCount > 0 && (
                  <div
                    data-testid="notifications-unread-badge"
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '2px',
                      minWidth: '18px',
                      height: '18px',
                      background: theme.colors.accent.salmon,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: '700',
                      color: '#FFF',
                      padding: '0 4px',
                    }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </div>
                )}
              </button>

              {notifOpen && (
                <div
                  data-testid="notifications-dropdown"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: theme.colors.bg.card,
                    borderRadius: theme.radius.card,
                    boxShadow: theme.colors.shadows.xl,
                    border: `1px solid ${theme.colors.borders}`,
                    width: '360px',
                    maxHeight: '420px',
                    overflowY: 'auto',
                    zIndex: 50,
                  }}
                >
                  <div style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${theme.colors.borders}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{ fontWeight: '600', color: theme.colors.text.primary, fontSize: '15px' }}>
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        data-testid="mark-all-read-btn"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: theme.colors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <CheckCheck size={14} /> Mark all read
                      </button>
                    )}
                  </div>

                  {notifications.length === 0 ? (
                    <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                      <Bell size={28} color={theme.colors.text.muted} style={{ margin: '0 auto 8px' }} />
                      <p style={{ color: theme.colors.text.muted, fontSize: '13px' }}>No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        data-testid={`notif-item-${n.id}`}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: n.is_read ? 'transparent' : `${theme.colors.primary}08`,
                          border: 'none',
                          borderBottom: `1px solid ${theme.colors.borders}`,
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'flex-start',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = theme.colors.bg.secondary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = n.is_read ? 'transparent' : `${theme.colors.primary}08`; }}
                      >
                        {!n.is_read && (
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.colors.primary, marginTop: '6px', flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: n.is_read ? '400' : '600', fontSize: '13px', color: theme.colors.text.primary, marginBottom: '2px' }}>
                            {n.title}
                          </p>
                          <p style={{ fontSize: '12px', color: theme.colors.text.muted, lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {n.body}
                          </p>
                          <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginTop: '4px' }}>
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                }}
                data-testid="profile-dropdown-trigger"
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent.teal})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFF',
                  fontWeight: '600',
                  fontSize: '14px',
                }}>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <ChevronDown size={16} color={theme.colors.text.secondary} />
              </button>

              {profileOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: theme.colors.bg.card,
                  borderRadius: theme.radius.card,
                  boxShadow: theme.colors.shadows.xl,
                  border: `1px solid ${theme.colors.borders}`,
                  minWidth: '220px',
                  overflow: 'hidden',
                  zIndex: 50,
                }}>
                  <div style={{ 
                    padding: '16px',
                    borderBottom: `1px solid ${theme.colors.borders}` 
                  }}>
                    <div style={{ 
                      fontWeight: '600',
                      color: theme.colors.text.primary,
                      marginBottom: '4px'
                    }}>
                      {user?.name}
                    </div>
                    <div style={{ 
                      fontSize: '13px',
                      color: theme.colors.text.muted 
                    }}>
                      {user?.email}
                    </div>
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: theme.colors.primary,
                      textTransform: 'capitalize',
                      fontWeight: '600'
                    }}>
                      {user?.role === 'broker' ? 'Portfolio Partner' : user?.role}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      navigate(`/${user?.role === 'creator' ? 'creator' : user?.role}/settings`);
                      setProfileOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '14px',
                      color: theme.colors.text.secondary,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme.colors.bg.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Settings size={18} />
                    Settings
                  </button>

                  <button
                    onClick={() => {
                      onLogout();
                      setProfileOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderTop: `1px solid ${theme.colors.borders}`,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '14px',
                      color: theme.colors.status.error,
                    }}
                    data-testid="logout-btn"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `${theme.colors.status.error}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main style={{
          flex: 1,
          padding: '32px',
          overflowY: 'auto',
        }}>
          {children}
        </main>
      </div>
      <GuidedTour user={user} />
    </div>
  );
};

export default AppLayout;
